import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';

import { API_SCOPE } from './config';

/**
 * The caller's app roles, read from the API access token.
 *
 * Not from the ID token, which is the obvious place and the wrong one. App roles are defined
 * on the *API* app registration, and Entra puts a `roles` claim only on tokens issued for the
 * app that defines them. The ID token is issued for the dashboard's own registration, which
 * defines no roles, so its `roles` claim is absent no matter who is assigned what — a user
 * with IRA.Admin and a user with nothing produce identical ID tokens.
 *
 * The alternative is to redefine the four roles on the dashboard registration and assign
 * every group twice. That is worse than it sounds for a console like this one: the two
 * assignment surfaces drift, and the failure is silent in both directions — a dashboard
 * offering an approve button the API then refuses, or hiding one it would have allowed.
 * Reading the API's own token means the badge and the enforcement cannot disagree, because
 * they are derived from the same claim in the same token.
 *
 * This is still presentation only. `roles.ts` explains why: the API re-derives the principal
 * from the verified token on every request. Nothing here is a control, and the token stays
 * opaque for transport — it is read for display, never validated, and never trusted.
 */

export interface RolesState {
  status: 'loading' | 'ready' | 'error';
  roles: readonly string[];
  /** Set when the token could not be acquired, for the sign-in screen to show. */
  error: string | null;
}

export function useRoles(): RolesState {
  const { instance } = useMsal();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

  const query = useQuery({
    queryKey: ['auth', 'roles', account?.homeAccountId ?? ''],
    enabled: Boolean(account),
    // The claim cannot change without a new token, and a new token means a new sign-in and a
    // fresh page. Refetching would spend a silent token acquisition to re-read a constant.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async (): Promise<readonly string[]> => {
      if (!account) return [];
      const result = await instance.acquireTokenSilent({ scopes: [API_SCOPE], account });
      return claimsOf(result.accessToken).roles ?? [];
    },
  });

  if (!account) return { status: 'ready', roles: [], error: null };
  if (query.isError) {
    const error = query.error;
    return {
      status: 'error',
      roles: [],
      error:
        error instanceof InteractionRequiredAuthError
          ? 'Your session needs renewing.'
          : `Could not read your access: ${String(error)}`,
    };
  }
  if (!query.isSuccess) return { status: 'loading', roles: [], error: null };
  return { status: 'ready', roles: query.data, error: null };
}

/**
 * Claims out of a JWT, without verifying it.
 *
 * Safe only because nothing here is an authorisation decision — a forged token would change
 * what this browser draws for its own user and nothing else, since the API verifies the
 * signature itself before acting on anything.
 */
function claimsOf(jwt: string): { roles?: string[] } {
  const payload = jwt.split('.')[1];
  if (!payload) return {};
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as { roles?: string[] };
  } catch {
    // A token we cannot read is reported as no roles, which shows the no-access screen
    // rather than crashing the app on a malformed claim set.
    return {};
  }
}
